import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Keyboard, Save, Loader2, Info, Zap, Target } from 'lucide-react';

const TypingTestSettings = () => {
    const { api, user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Only show for admin/trainer
    const isAdmin = user?.role === 'admin' || user?.role === 'trainer';

    useEffect(() => {
        if (isAdmin) {
            fetchSettings();
        }
    }, [isAdmin]);

    const fetchSettings = async () => {
        try {
            const response = await api.get('/rpat/typing-tests/admin/settings');
            setSettings(response.data);
        } catch (error) {
            console.error('Failed to load typing test settings:', error);
            // Set defaults
            setSettings({
                global_min_wpm: 36,
                global_min_accuracy: 92.0,
                use_global_settings: true,
                wpm_range: { min: 25, max: 48 },
                accuracy_range: { min: 90, max: 95 }
            });
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.put(`/rpat/typing-tests/admin/settings?global_min_wpm=${settings.global_min_wpm}&global_min_accuracy=${settings.global_min_accuracy}&use_global_settings=${settings.use_global_settings}`);
            toast.success('Typing test settings saved successfully!');
        } catch (error) {
            console.error('Failed to save settings:', error);
            toast.error(error.response?.data?.detail || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (!isAdmin) {
        return null;
    }

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card data-testid="typing-test-settings-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Keyboard className="h-5 w-5 text-primary" />
                    R-PAT Typing Test Settings
                </CardTitle>
                <CardDescription>
                    Control the WPM and accuracy requirements for all typing tests in R-PAT
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Info Banner */}
                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                        <p className="font-medium mb-1">Global Settings Mode</p>
                        <p>These settings apply to all 10 typing tests. Users must meet both WPM and accuracy thresholds to pass.</p>
                    </div>
                </div>

                {/* WPM Setting */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <Label className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-amber-500" />
                                Minimum Words Per Minute (WPM)
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                Speed requirement: {settings?.wpm_range?.min} - {settings?.wpm_range?.max} WPM
                            </p>
                        </div>
                        <Badge variant="outline" className="text-lg font-mono px-3 py-1">
                            {settings?.global_min_wpm} WPM
                        </Badge>
                    </div>
                    <Slider
                        value={[settings?.global_min_wpm || 36]}
                        onValueChange={([value]) => setSettings({ ...settings, global_min_wpm: value })}
                        min={25}
                        max={48}
                        step={1}
                        className="w-full"
                        data-testid="wpm-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>25 WPM (Easier)</span>
                        <span>48 WPM (Harder)</span>
                    </div>
                </div>

                {/* Accuracy Setting */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <Label className="flex items-center gap-2">
                                <Target className="h-4 w-4 text-emerald-500" />
                                Minimum Accuracy
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                Accuracy requirement: {settings?.accuracy_range?.min}% - {settings?.accuracy_range?.max}%
                            </p>
                        </div>
                        <Badge variant="outline" className="text-lg font-mono px-3 py-1">
                            {settings?.global_min_accuracy}%
                        </Badge>
                    </div>
                    <Slider
                        value={[settings?.global_min_accuracy || 92]}
                        onValueChange={([value]) => setSettings({ ...settings, global_min_accuracy: value })}
                        min={90}
                        max={95}
                        step={0.5}
                        className="w-full"
                        data-testid="accuracy-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                        <span>90% (Easier)</span>
                        <span>95% (Harder)</span>
                    </div>
                </div>

                {/* Current Settings Summary */}
                <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Current Requirements Summary</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-amber-500" />
                            <span>Speed: <strong>{settings?.global_min_wpm} WPM</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-emerald-500" />
                            <span>Accuracy: <strong>{settings?.global_min_accuracy}%</strong></span>
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        Applied to all 10 typing tests in R-PAT
                    </p>
                </div>

                {/* Save Button */}
                <Button
                    onClick={saveSettings}
                    disabled={saving}
                    className="w-full"
                    data-testid="save-typing-settings-btn"
                >
                    {saving ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save className="mr-2 h-4 w-4" />
                            Save Typing Test Settings
                        </>
                    )}
                </Button>
            </CardContent>
        </Card>
    );
};

export default TypingTestSettings;
